// NEXT VALUE FOR slot detector (Workstream C, mssql 42.§3/§4/§6/§7).
//
// `NEXT VALUE FOR ▮` names a SEQUENCE, but the clause machine classifies the
// position as `from` (bare) or generic dotted-access (`dbo.▮`) and emitted
// tables. Same text-walk shape as `isAtExecTargetSlot`: matches when the
// caret sits after `NEXT VALUE FOR` with at most a partial (possibly
// schema-qualified) identifier.
//
// The companion `scanBufferCreatedSequences` surfaces sequences whose
// `CREATE SEQUENCE` statement lives in the same buffer but hasn't been
// executed yet (the snapshot can't know them) — mirrors oracle's
// `isBufferLocalOracleSequence`.

import { stripCommentsPreserving } from './sqlClauseToPositionKind';

// Tail = NEXT VALUE FOR followed only by an optional in-progress identifier
// (schema-qualified, [bracketed], "quoted").
const NEXT_VALUE_FOR_TAIL_RE =
  /\bNEXT\s+VALUE\s+FOR\s+(?:[A-Za-z_["][\w.$[\]"]*)?$/i;

export function isAtNextValueForSlot(
  text: string,
  offset: number,
  statementStart: number,
): boolean {
  const slice = stripCommentsPreserving(text.slice(statementStart, offset));
  return NEXT_VALUE_FOR_TAIL_RE.test(slice);
}

export type BufferSequence = {
  readonly name: string;
  readonly schema: string | null;
};

// `IF NOT EXISTS` is optional so the PG spelling (`CREATE SEQUENCE IF NOT
// EXISTS public.acact_invoice_seq`) reads the NAME rather than the word `IF`.
// T-SQL has no such clause, so the group never matches there.
const CREATE_SEQUENCE_RE =
  /\bCREATE\s+SEQUENCE\s+(?:IF\s+NOT\s+EXISTS\s+)?((?:\[[^\]]*\]|"[^"]*"|[A-Za-z_][\w$#]*)(?:\.(?:\[[^\]]*\]|"[^"]*"|[A-Za-z_][\w$#]*))*)/gi;

/** All `CREATE SEQUENCE <name>` declarations in the buffer (whole buffer —
 *  a sequence created above OR below the caret is a likely completion). */
export function scanBufferCreatedSequences(text: string): BufferSequence[] {
  const out: BufferSequence[] = [];
  const stripped = stripCommentsPreserving(text);
  let m: RegExpExecArray | null;
  CREATE_SEQUENCE_RE.lastIndex = 0;
  while ((m = CREATE_SEQUENCE_RE.exec(stripped)) !== null) {
    const parts = splitQualifiedName(m[1]);
    const name = parts[parts.length - 1] ?? '';
    if (!name) continue;
    const schema = parts.length >= 2 && parts[parts.length - 2] !== ''
      ? parts[parts.length - 2]
      : null;
    out.push({ name, schema });
  }
  return out;
}

/** Split `db.schema.seq` on dots, de-bracketing `[…]` / `"…"` parts. */
function splitQualifiedName(qualified: string): string[] {
  const parts: string[] = [];
  let cur = '';
  let inBracket = false;
  let inQuote = false;
  for (const ch of qualified) {
    if (inBracket) {
      if (ch === ']') inBracket = false;
      else cur += ch;
      continue;
    }
    if (inQuote) {
      if (ch === '"') inQuote = false;
      else cur += ch;
      continue;
    }
    if (ch === '[') { inBracket = true; continue; }
    if (ch === '"') { inQuote = true; continue; }
    if (ch === '.') { parts.push(cur); cur = ''; continue; }
    cur += ch;
  }
  parts.push(cur);
  return parts;
}
